/**
 * Energy Insights API Client — GET /api/user/energy-insights.
 *
 * Authenticated, account-scoped monthly rollup. Same conventions as
 * `userSessionsApi.js`: relative path (authClient already carries the
 * API baseURL), correlation-id header for tracing, no userId ever sent
 * over the wire — identity is JWT-derived on the backend.
 */

import { authClient } from '../auth/authClient.js';

const INSIGHTS_BASE = '/user/energy-insights';

const MONTH_RE = /^\d{4}-\d{2}$/;

function correlationId() {
  return `ev-user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Fetch a month of energy insights.
 *
 * @param {{ month?: string }} [opts]
 *   `month` is optional; the backend defaults to the current local
 *   month (America/Barbados). When supplied it MUST be `YYYY-MM`.
 * @returns {Promise<
 *   | { ok: true, month: string, timezone: string, summary: object, daily: object[], recentSessions: object[], range: { startDate: string, endDate: string }, correlationId: string }
 *   | { ok: false, status?: number, error: string, message: string, correlationId: string }
 * >}
 */
export async function getEnergyInsights({ month } = {}) {
  const cid = correlationId();

  if (month !== undefined && (typeof month !== 'string' || !MONTH_RE.test(month))) {
    return {
      ok: false,
      status: 400,
      error: 'INVALID_MONTH',
      message: 'That month is not valid.',
      correlationId: cid,
    };
  }

  try {
    const res = await authClient.get(INSIGHTS_BASE, {
      params: month ? { month } : undefined,
      headers: { 'X-Correlation-Id': cid },
    });
    const body = res.data || {};
    return {
      ok: true,
      month: typeof body.month === 'string' ? body.month : month || null,
      timezone: typeof body.timezone === 'string' ? body.timezone : 'America/Barbados',
      range: body.range || null,
      summary: body.summary || {
        energyKWh: 0, sessionCount: 0, durationMinutes: 0,
        totalCost: 0, averageEnergyKWh: 0,
      },
      daily: Array.isArray(body.daily) ? body.daily : [],
      recentSessions: Array.isArray(body.recentSessions) ? body.recentSessions : [],
      correlationId: cid,
    };
  } catch (err) {
    const status = err?.response?.status;
    const data = err?.response?.data || {};
    return {
      ok: false,
      status,
      error: data.error || 'INSIGHTS_LOOKUP_FAILED',
      message:
        data.message || err?.message || 'Could not load your energy insights.',
      correlationId: cid,
    };
  }
}
